if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.quantityInput = this.form.elements.namedItem('quantity');
        this.cartLimitPanel = this.querySelector('[data-cart-limit]');
        this.cartLimitLink = this.querySelector('[data-cart-limit-link]');

        this.cartLimitLink?.addEventListener('click', (event) => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
          event.preventDefault();
          cartDrawer.open(this.cartLimitLink);
        });

        if (this.quantityInput) {
          const clearInventoryError = () => {
            if (!this.inventoryError) return;
            this.inventoryError = false;
            this.handleErrorMessage(false, true);
          };
          this.quantityInput.addEventListener('input', clearInventoryError);
          this.quantityInput.addEventListener('change', clearInventoryError);
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const items = event?.cartData?.items;
          const variantId = Number(this.variantIdInput.value);

          if (Array.isArray(items) && Number.isFinite(Number(event?.cartData?.item_count))) {
            const cartQuantity = items.reduce(
              (total, item) => total + (Number(item.variant_id) === variantId ? Number(item.quantity) : 0),
              0
            );
            this.variantIdInput.dataset.cartQuantity = String(cartQuantity);
            this.updateInventoryState();
            return;
          }

          if (Number(event?.productVariantId) !== variantId) return;
          const addedQuantity = Number(event?.addedQuantity);
          const currentQuantity = Number(this.variantIdInput.dataset.cartQuantity || 0);
          if (!Number.isFinite(addedQuantity) || !Number.isFinite(currentQuantity)) return;
          this.variantIdInput.dataset.cartQuantity = String(currentQuantity + addedQuantity);
          this.updateInventoryState();
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;
          this.updateInventoryState();
        });

        this.updateInventoryState();
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        if (!this.validateInventory(formData)) return;
        this.error = false;
        this.inventoryError = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.renderCartContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.renderCartContents(response);
            }

            this.showSuccessMessage();
            this.publishCartUpdate(formData, response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && !this.inventoryLimitReached) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      renderCartContents(response) {
        try {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
        } catch (error) {
          console.error('The item was added, but the cart could not be rendered.', error);
          const refreshPromise = document.querySelector('cart-drawer-items')?.onCartUpdate?.();
          Promise.resolve(refreshPromise)
            .then(() => this.cart.open?.())
            .catch((refreshError) => console.error('The cart could not be refreshed.', refreshError));
        }
      }

      publishCartUpdate(formData, response) {
        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

        Promise.resolve()
          .then(() =>
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              addedQuantity: parseInt(formData.get('quantity') || '1', 10),
              cartData: response,
            })
          )
          .then(() => CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker))
          .catch((error) => console.error('The cart was updated, but a cart subscriber failed.', error));
      }

      showSuccessMessage() {
        if (!this.submitButton.classList.contains('product-form__submit')) return;

        const originalText = this.submitButtonText.textContent;
        const addedText = window.eclatStrings?.added || 'Added';
        this.submitButtonText.textContent = `${addedText} ✓`;
        clearTimeout(this.successMessageTimer);
        this.successMessageTimer = setTimeout(() => {
          if (this.submitButtonText.textContent === `${addedText} ✓`) {
            this.submitButtonText.textContent = originalText;
          }
        }, 1600);
      }

      updateInventoryState() {
        if (!this.cartLimitPanel) return;

        const variantInput = this.variantIdInput;
        const inventoryQuantity = parseInt(variantInput.dataset.inventoryQuantity, 10);
        const cartQuantity = parseInt(variantInput.dataset.cartQuantity || '0', 10);
        const tracksInventory = variantInput.dataset.inventoryManagement === 'shopify';
        const stopsAtInventory = variantInput.dataset.inventoryPolicy !== 'continue';
        const limitReached =
          tracksInventory &&
          stopsAtInventory &&
          Number.isFinite(inventoryQuantity) &&
          Number.isFinite(cartQuantity) &&
          cartQuantity > 0 &&
          cartQuantity >= inventoryQuantity;

        this.inventoryLimitReached = limitReached;
        this.classList.toggle('is-cart-limit-reached', limitReached);
        this.cartLimitPanel.hidden = !limitReached;
        this.submitButton.hidden = limitReached;

        if (limitReached) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (this.cart?.activeElement === this.submitButton && typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(this.cartLimitLink);
          }
          this.inventoryError = false;
          this.handleErrorMessage(false, true);
          return;
        }

        const purchasable = variantInput.dataset.variantPurchasable === 'true';
        this.submitButton.disabled = !purchasable;
        if (purchasable) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      validateInventory(formData) {
        const variantInput = this.variantIdInput;
        if (
          variantInput.dataset.inventoryManagement !== 'shopify' ||
          variantInput.dataset.inventoryPolicy === 'continue'
        ) {
          return true;
        }

        const inventoryQuantity = parseInt(variantInput.dataset.inventoryQuantity, 10);
        const cartQuantity = parseInt(variantInput.dataset.cartQuantity || '0', 10);
        const requestedQuantity = parseInt(formData.get('quantity') || '1', 10);

        if (
          Number.isNaN(inventoryQuantity) ||
          Number.isNaN(cartQuantity) ||
          Number.isNaN(requestedQuantity) ||
          cartQuantity + requestedQuantity <= inventoryQuantity
        ) {
          return true;
        }

        const availableToAdd = Math.max(inventoryQuantity - cartQuantity, 0);
        const message = window.cartStrings.quantityError.replace('[quantity]', availableToAdd);
        this.inventoryError = true;
        this.handleErrorMessage(message, true);
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: message,
          message,
        });

        this.quantityInput?.focus();
        this.quantityInput?.select();
        return false;
      }

      handleErrorMessage(errorMessage = false, force = false) {
        if (this.hideErrors && !force) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper && force) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');

          const messageElement = document.createElement('span');
          messageElement.className = 'product-form__error-message';
          this.errorMessageWrapper.appendChild(messageElement);
          this.form.before(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!disable && this.inventoryLimitReached) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
